/**
 * Enhanced Web Vitals Reporting with RUM Collection
 * Collects LCP, CLS, INP, TTFB, FCP and sends to backend
 */

import type { Metric } from 'web-vitals';
import { BUSINESS_IDENTITY } from '@/config/businessIdentity';

interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  os: string;
  screenWidth: number;
  screenHeight: number;
  connectionType?: string;
  effectiveBandwidth?: number;
}

interface MetricPayload {
  metrics: Array<{
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType?: string;
    entries?: number;
    attribution?: Record<string, unknown>;
  }>;
  route: {
    path: string;
    name?: string;
  };
  device: DeviceInfo;
  session?: {
    id?: string;
    userId?: string;
    orgId?: string;
  };
  timestamp: number;
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
};

type MetricWithAttribution = Metric & {
  attribution?: Record<string, unknown>;
};

// Batched metrics queue
let metricsQueue: MetricPayload['metrics'] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

const canonicalHostname = new URL(BUSINESS_IDENTITY.website).hostname.toLowerCase();

export const shouldReportWebVitals = (hostname: string, explicitlyEnabled = false) => (
  explicitlyEnabled || hostname.toLowerCase() === canonicalHostname
);

// Get device info
const getDeviceInfo = (): DeviceInfo => {
  const width = window.innerWidth;
  const ua = navigator.userAgent;
  
  // Determine device type
  let type: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (width < 768) type = 'mobile';
  else if (width < 1024) type = 'tablet';
  
  // Parse browser
  let browser = 'Unknown';
  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  // Parse OS
  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  // Get connection info if available
  const connection = (navigator as NavigatorWithConnection).connection;
  
  return {
    type,
    browser,
    os,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    connectionType: connection?.effectiveType,
    effectiveBandwidth: connection?.downlink,
  };
};

// Get session info
const getSessionInfo = () => {
  // Get or create session ID
  let sessionId = sessionStorage.getItem('dialgia_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('dialgia_session_id', sessionId);
  }
  
  return {
    id: sessionId,
    userId: undefined, // Would be populated from auth context
    orgId: undefined,
  };
};

// Flush metrics to the backend
const flushMetrics = async () => {
  if (metricsQueue.length === 0) return;
  
  const metrics = [...metricsQueue];
  metricsQueue = [];
  
  const payload: MetricPayload = {
    metrics,
    route: {
      path: window.location.pathname,
      name: document.title,
    },
    device: getDeviceInfo(),
    session: getSessionInfo(),
    timestamp: Date.now(),
  };
  
  try {
    // Use sendBeacon for reliability during page unload
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/collect-webvitals`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback to fetch with keepalive
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {});
    }
  } catch (error) {
    // Silent fail - don't affect user experience
  }
};

// Queue metric for batched sending
const queueMetric = (metric: Metric) => {
  metricsQueue.push({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    entries: metric.entries?.length,
    attribution: (metric as MetricWithAttribution).attribution,
  });
  
  // Debounce flush - wait for all metrics to be collected
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushMetrics, 3000);
};

// Enhanced reporting with RUM collection
export const reportWebVitals = () => {
  // Keep previews, local audits, and third-party hosts out of production RUM.
  if (!shouldReportWebVitals(window.location.hostname, Boolean(import.meta.env.VITE_ENABLE_RUM))) {
    return;
  }
  
  // Use requestIdleCallback to avoid impacting performance
  const scheduleLoad = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 5000 });
    } else {
      setTimeout(callback, 100);
    }
  };
  
  scheduleLoad(() => {
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      // Subscribe to all core web vitals
      onCLS(queueMetric, { reportAllChanges: false });
      onINP(queueMetric, { reportAllChanges: false });
      onFCP(queueMetric);
      onLCP(queueMetric, { reportAllChanges: false });
      onTTFB(queueMetric);
      
      // Flush on page visibility change (user leaving)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          flushMetrics();
        }
      });
      
      // Flush before page unload
      window.addEventListener('pagehide', flushMetrics);
      
    }).catch(() => {
      // Silent fail if web-vitals is not available
    });
  });
};

// Export for manual triggering
export { flushMetrics, getDeviceInfo };
